import React from 'react';
import _ from 'underscore';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { N2dAdRenderer } from './n2d-ad-renderer';
import { withUAPLThemedStyles } from '../with-themed-styles';
import { getVideoPlacementStyles } from '../styles/ad-placement-components/video-placement-styles';
import type { UAPPlacementComponentProps, UAPPlacementTypeValue } from '../@types';

export const UC_N2D_PREVIEW_MAX_WIDTH = 520;
export const UC_N2D_PREVIEW_MAX_HEIGHT = 520;

/*
 * Derive display info from the placement type string (e.g., 'UC_Display_300x250' → { size: '300x250' }).
 * Used as fallback when displayInfo is not attached to placementData.
 */

const deriveDisplayInfo = (placementType?: UAPPlacementTypeValue | '') => {
  if (!placementType || !placementType.startsWith('UC_Display_')) return null;
  const suffix = placementType.replace('UC_Display_', '');
  if (suffix === 'Selected') return { size: '_selected', name: 'Selected display asset' };
  return { size: suffix, name: '' };
};

/*
 * UC display ad card component for N2D ad preview rendering.
 * Uses the AdPreview SDK (via N2dAdRenderer) to render a full ad card
 * (image + headline + CTA + branding) instead of a simple cropped image.
 *
 * Reads display-specific data from placementData (attached by buildExtraPlacements):
 *   - placementData.displayInfo: { size, name }
 *   - placementData.renderMode: 'image' | 'displayAsset' (per-item mode)
 *   - placementData.selectedDisplayAsset: { url, size } or null
 *   - placementData.lastVisualSelectionType: 'image' | 'displayAsset' (fallback for renderMode)
 *   - placementData.allDisplayInfo: full UC_DISPLAY_EPT_INFO map (for closest-size matching)
 * Falls back to deriving displayInfo from placementMeta.placementType when data is not attached.
 * Reads raw image data from previewedImageData prop (passed by UAPL).
 * Reads ad text from props spread by UAPL's onRenderPlacement (shortHeadline, longHeadline, etc.).
 */
export const UCDisplayPlacementInner = ({
  classes,
  placementData,
  previewedImageData,
  i18n,
  shortHeadline,
  longHeadline,
  description,
  businessName,
  callToAction,
  finalUrl,
}: UAPPlacementComponentProps) => {
  const {
    displayInfo: attachedDisplayInfo, selectedDisplayAsset, lastVisualSelectionType, allDisplayInfo,
    renderMode: itemRenderMode,
  } = placementData || {};

  // Derive displayInfo from placement type if not explicitly attached
  const displayInfo = attachedDisplayInfo
    || deriveDisplayInfo(placementData && placementData.placementMeta && placementData.placementMeta.placementType);

  if (!displayInfo) return null;

  const { size: dpSize } = displayInfo;
  const isSelected = dpSize === '_selected';

  // isImageMode selects which source this display placement renders:
  //   - true  (image mode): the user supplied a RAW image. We pull it from
  //     previewedImageData (picking the best-fit display size for the
  //     `_selected` case) and render a COMPLETE ad creative via N2dAdRenderer
  //     (image + headline + CTA + branding) at the target placement size.
  //   - false (display-asset mode): the user picked a pre-built display asset,
  //     which is already a finished creative. We take its URL from
  //     selectedDisplayAsset and render it directly inside a target-sized box
  //     (object-fit: cover / cropped), not through the ad builder.
  // Per-item renderMode takes priority over the global lastVisualSelectionType
  // fallback, which defaults to image mode unless the last selection was a
  // display asset.
  const isImageMode = itemRenderMode
    ? itemRenderMode === 'image'
    : lastVisualSelectionType !== 'displayAsset';

  let targetW;
  let targetH;
  let imageUrl = null;

  if (isSelected) {
    if (isImageMode && previewedImageData) {
      // Find best-fit display size for the image
      const imgW = previewedImageData.originalWidth;
      const imgH = previewedImageData.originalHeight;
      const imgAR = imgW / imgH;
      let bestKey = null;
      let bestDiff = Infinity;
      _.each(allDisplayInfo || {}, (info, key) => {
        if (info.size === '_selected') return;
        const [sw, sh] = info.size.split('x').map(Number);
        if (imgW >= sw && imgH >= sh) {
          const diff = Math.abs((sw / sh) - imgAR);
          if (diff < bestDiff) { bestDiff = diff; bestKey = key; }
        }
      });
      if (bestKey) {
        const bestInfo = allDisplayInfo[bestKey];
        const [bw, bh] = bestInfo.size.split('x').map(Number);
        targetW = bw;
        targetH = bh;
        imageUrl = previewedImageData.url;
      }
    } else if (!isImageMode && selectedDisplayAsset) {
      const [aw, ah] = (selectedDisplayAsset.size || '').split('x').map(Number);
      if (aw && ah) {
        targetW = aw;
        targetH = ah;
        imageUrl = selectedDisplayAsset.url;
      }
    }
  } else {
    [targetW, targetH] = dpSize.split('x').map(Number);

    if (isImageMode && previewedImageData) {
      imageUrl = previewedImageData.url;
    } else if (!isImageMode && selectedDisplayAsset) {
      if (selectedDisplayAsset.size === dpSize) {
        imageUrl = selectedDisplayAsset.url;
      }
    }
  }

  // Build preview texts for the N2D SDK
  const previewTexts = {
    longHeadline: longHeadline || shortHeadline || '',
    businessName: businessName || '',
    description: description || '',
    callToAction: callToAction || '',
    url: finalUrl || '',
  };

  return (
    <div className={classNames(classes.placementCard, 'ad-placement-card-preview')}>
      <div style={{ textAlign: 'center', padding: '12px' }}>
        {imageUrl && targetW && targetH ? (
          (() => {
            if (isImageMode) {
              return (
                <N2dAdRenderer
                  adWidth={targetW}
                  adHeight={targetH}
                  imageUrl={imageUrl}
                  imageWidth={previewedImageData ? previewedImageData.originalWidth : undefined}
                  imageHeight={previewedImageData ? previewedImageData.originalHeight : undefined}
                  previewTexts={previewTexts}
                  maxWidth={UC_N2D_PREVIEW_MAX_WIDTH}
                  maxHeight={UC_N2D_PREVIEW_MAX_HEIGHT}
                  i18n={i18n}
                />
              );
            }
            // A display asset is already a complete ad creative -- render it
            // inside a target-sized box (scaled to fit) and crop (object-fit:
            // cover) so the image keeps its own aspect ratio instead of being
            // stretched/compacted into the target dimensions.
            const maxW = UC_N2D_PREVIEW_MAX_WIDTH;
            const maxH = UC_N2D_PREVIEW_MAX_HEIGHT;
            const fitScale = Math.min(maxW / targetW, maxH / targetH);
            const boxW = Math.round(targetW * fitScale);
            const boxH = Math.round(targetH * fitScale);
            return (
              <div style={{
                width: boxW,
                height: boxH,
                margin: '0 auto',
                overflow: 'hidden',
                boxShadow: '0 2px 4px 0 rgba(0, 0, 0, 0.14), 0 0 2px 0 rgba(0, 0, 0, 0.12)',
              }}
              >
                <img
                  src={imageUrl}
                  alt=""
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    display: 'block',
                  }}
                />
              </div>
            );
          })()
        ) : (
          <div style={{
            width: '100%',
            height: 120,
            backgroundColor: '#f5f5f5',
            border: '1px dashed #d2d0ce',
            borderRadius: '4px',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#999',
            fontSize: '13px',
          }}
          >
            {i18n ? i18n.getString(_TL_('No display asset')) : 'No display asset'}
          </div>
        )}
      </div>
    </div>
  );
};

UCDisplayPlacementInner.propTypes = {
  classes: PropTypes.shape({
    placementCard: PropTypes.string,
  }),
  placementData: PropTypes.shape({
    placementMeta: PropTypes.shape({
      placementType: PropTypes.string,
    }),
    displayInfo: PropTypes.shape({
      size: PropTypes.string,
      name: PropTypes.string,
    }),
    renderMode: PropTypes.oneOf(['image', 'displayAsset']),
    selectedDisplayAsset: PropTypes.shape({
      url: PropTypes.string,
      size: PropTypes.string,
    }),
    lastVisualSelectionType: PropTypes.string,
    allDisplayInfo: PropTypes.shape({}),
  }),
  previewedImageData: PropTypes.shape({
    url: PropTypes.string,
    originalWidth: PropTypes.number,
    originalHeight: PropTypes.number,
  }),
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }),
  shortHeadline: PropTypes.string,
  longHeadline: PropTypes.string,
  description: PropTypes.string,
  businessName: PropTypes.string,
  callToAction: PropTypes.string,
  finalUrl: PropTypes.string,
};

UCDisplayPlacementInner.defaultProps = {
  classes: {},
  placementData: null,
  previewedImageData: null,
  i18n: null,
  shortHeadline: '',
  longHeadline: '',
  description: '',
  businessName: '',
  callToAction: '',
  finalUrl: '',
};

export const UCDisplayPlacement = withUAPLThemedStyles(

  (props: UAPPlacementComponentProps) => <UCDisplayPlacementInner {...props} />,
  getVideoPlacementStyles,
  { componentName: 'UCDisplayPlacement' }
);
